import { Monitor, Moon, Sun } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { THEME_PREFERENCES, type ThemePreference, useTheme } from '../../providers/theme-context'

const ICONS: Record<ThemePreference, LucideIcon> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
}

const LABEL_KEY = {
  system: 'theme.system',
  light: 'theme.light',
  dark: 'theme.dark',
} as const

type ThemeToggleProps = {
  className?: string
  /** Renders the three options side by side instead of a single cycling button. */
  expanded?: boolean
}

/**
 * System / light / dark. The compact form cycles through the three on click and
 * announces the newly selected mode via a live region; the expanded form (used
 * in the mobile sheet) shows all three as a radio group.
 */
export const ThemeToggle = ({ className, expanded = false }: ThemeToggleProps) => {
  const { t } = useTranslation()
  const { preference, setPreference } = useTheme()

  if (expanded) {
    return (
      <div
        role="radiogroup"
        aria-label={t('theme.label')}
        className={cn('inline-flex w-full items-center rounded-full border border-border bg-card p-1', className)}
      >
        {THEME_PREFERENCES.map((option) => {
          const Icon = ICONS[option]
          const active = option === preference

          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setPreference(option)}
              className={cn(
                'touch-target flex flex-1 items-center justify-center gap-2 rounded-full px-3 type-body-sm font-semibold',
                'transition-colors duration-hover ease-out',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Icon aria-hidden="true" className="size-4" />
              {t(LABEL_KEY[option])}
            </button>
          )
        })}
      </div>
    )
  }

  const currentIndex = THEME_PREFERENCES.indexOf(preference)
  const next = THEME_PREFERENCES[(currentIndex + 1) % THEME_PREFERENCES.length] ?? 'light'
  const Icon = ICONS[preference]

  return (
    <button
      type="button"
      onClick={() => setPreference(next)}
      title={t('theme.current', { mode: t(LABEL_KEY[preference]) })}
      aria-label={t('theme.switchTo', { mode: t(LABEL_KEY[next]) })}
      className={cn(
        'touch-target inline-flex items-center justify-center rounded-full border border-border bg-card text-muted-foreground',
        'transition-colors duration-hover ease-out hover:bg-muted hover:text-foreground',
        className,
      )}
    >
      <Icon aria-hidden="true" className="size-5" />
      <span role="status" className="sr-only">
        {t('theme.current', { mode: t(LABEL_KEY[preference]) })}
      </span>
    </button>
  )
}
